"use client";

/**
 * Motion system foundation (build-prompt Phase 4) — reveal-on-scroll.
 *
 * useReveal() — attach the returned ref to any element and it fades/slides
 * in (14px translate-up + fade, `.rv` classes in globals.css) the first
 * time it enters the viewport. Fires ONCE per element.
 *
 *   const ref = useReveal<HTMLDivElement>();
 *   return <div ref={ref}>…</div>;
 *
 * Design points (per the Phase 4 spec):
 * - ONE IntersectionObserver shared across every useReveal element in the
 *   app (module-level singleton), not one observer per element.
 * - 60ms sibling stagger: elements revealed in the same observer tick are
 *   sorted by viewport position and get an incremental transition-delay.
 * - The hook adds the `.rv` class itself during the ref commit (before
 *   first paint), so server HTML ships WITHOUT it — crawlers and no-JS
 *   users see fully visible content; JS users get the reveal.
 * - prefers-reduced-motion: the completed end state renders immediately
 *   (and the CSS override in globals.css independently guarantees it).
 *
 * Caveat: classes are managed via el.classList. If the element's
 * `className` prop changes reactively after mount, include "rv" in your
 * own className so a re-render can't clobber the hidden state mid-reveal.
 */

import { useCallback, useEffect, useRef, type RefCallback } from "react";
import { prefersReducedMotion } from "./reduced-motion";

const STAGGER_MS = 60;
/** Slow duration (600ms) + max expected stagger headroom for delay cleanup. */
const DELAY_CLEANUP_MS = 700;

let sharedObserver: IntersectionObserver | null = null;

function getSharedObserver(): IntersectionObserver {
  if (sharedObserver) return sharedObserver;
  sharedObserver = new IntersectionObserver(
    (entries, obs) => {
      const revealing = entries
        .filter((e) => e.isIntersecting)
        .sort(
          (a, b) =>
            a.boundingClientRect.top - b.boundingClientRect.top ||
            a.boundingClientRect.left - b.boundingClientRect.left
        );
      revealing.forEach((entry, i) => {
        const el = entry.target as HTMLElement;
        obs.unobserve(el); // fire once
        el.style.transitionDelay = `${i * STAGGER_MS}ms`;
        el.classList.add("rv-in");
        // Clear the stagger delay once the reveal finishes so unrelated
        // later transitions on the same element aren't delayed.
        window.setTimeout(() => {
          el.style.transitionDelay = "";
        }, i * STAGGER_MS + DELAY_CLEANUP_MS);
      });
    },
    // threshold 0 + generous rootMargin: reveal as soon as any part nears
    // the viewport (never gate on a % of a tall/zero-height element).
    { rootMargin: "300px 0px 300px 0px", threshold: 0 }
  );
  return sharedObserver;
}

/**
 * Returns a ref callback. Attach it to the element you want revealed.
 * Each call site = one revealed element; for a list, call the hook in the
 * child component (or use `useReveal()` per item via a small wrapper).
 */
export function useReveal<T extends HTMLElement = HTMLElement>(): RefCallback<T> {
  const elRef = useRef<T | null>(null);

  // Unobserve on unmount (ref callback with null also handles this, but
  // React 18 may skip null calls in some concurrent edge cases).
  useEffect(() => {
    return () => {
      if (elRef.current && sharedObserver) sharedObserver.unobserve(elRef.current);
    };
  }, []);

  return useCallback((node: T | null) => {
    if (elRef.current && sharedObserver) sharedObserver.unobserve(elRef.current);
    elRef.current = node;
    if (!node) return;

    node.classList.add("rv");

    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      // Completed end state, no animation.
      node.classList.add("rv-in");
      return;
    }
    if (node.classList.contains("rv-in")) return; // already revealed
    getSharedObserver().observe(node);
    // Safety net: never leave content stuck invisible (fast scroll / lazy
    // mount races). Reveal anyway shortly after mount if the observer
    // hasn't fired yet — adding rv-in twice is harmless.
    window.setTimeout(() => node.classList.add("rv-in"), 1100);
  }, []);
}
